import { Injectable } from '@angular/core'
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpHeaders
} from '@angular/common/http'
import { Observable, Subject, throwError } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { urls } from '@constants/urls'
import { koodistoSources } from '@constants/koodisto-sources'
import { CreateCollectionPost } from '@models/collections/create-collection-post'
import { CreateCollectionResponse } from '@models/collections/create-collection-response'
import { AddToCollectionResponse } from '@models/collections/add-to-collection-response'
import { AddToCollectionPost } from '@models/collections/add-to-collection-post'
import { UserCollection } from '@models/collections/user-collection'
import { UserCollectionResponse } from '@models/collections/user-collection-response'
import { RemoveFromCollectionPost } from '@models/collections/remove-from-collection-post'
import { RemoveFromCollectionResponse } from '@models/collections/remove-from-collection-response'
import { CollectionResponse } from '@models/collections/collection-response'
import {
  CollectionForm,
  CollectionFormMaterial,
  CollectionFormMaterialAndHeading,
  CollectionFormMaterialAuthor
} from '@models/collections/collection-form'
import { UpdateCollectionPut } from '@models/collections/update-collection-put'
import { AlignmentObjectExtended } from '@models/alignment-object-extended'
import { Collection } from '@models/collections/collection'
import { AlignmentObjects } from '@models/alignment-objects'
import { UploadMessage } from '@models/upload-message'
import { CollectionCard } from '@models/collections/collection-card'
import {
  RecentCollectionResponse,
  RecentCollectionsResponse
} from '@models/collections/recent-collections-response'

@Injectable({
  providedIn: 'root'
})
export class CollectionService {
  public userCollections$ = new Subject<UserCollection[]>()
  public privateUserCollections$ = new Subject<UserCollection[]>()
  public publicUserCollections$ = new Subject<UserCollection[]>()
  public collection$ = new Subject<Collection>()
  public editCollection$ = new Subject<CollectionForm>()
  public recentCollections$ = new Subject<CollectionCard[]>()

  constructor(private http: HttpClient) {}

  /**
   * Handles errors.
   * @param {HttpErrorResponse} error
   * @private
   */
  private handleError(error: HttpErrorResponse) {
    console.error(error)

    return throwError('Something bad happened; please try again later.')
  }

  /**
   * Creates new collection.
   * @param {CreateCollectionPost} payload
   * @returns {Observable<CreateCollectionResponse>} Response
   */
  createCollection(payload: CreateCollectionPost): Observable<CreateCollectionResponse> {
    return this.http
      .post<CreateCollectionResponse>(`${urls.backendUrl}/collection/create`, payload, {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      })
      .pipe(catchError(this.handleError))
  }

  /**
   * Updates array of collections created by user.
   */
  updateUserCollections(): void {
    this.http
      .get<UserCollectionResponse>(`${urls.backendUrl}/collection/userCollection`, {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      })
      .subscribe((userCollectionResponse: UserCollectionResponse) => {
        const privateCollections: UserCollection[] = []
        const publicCollections: UserCollection[] = []

        // set all user collections
        this.userCollections$.next(userCollectionResponse.collections)

        userCollectionResponse.collections.forEach((collection: UserCollection) => {
          if (collection.publishedat === null) {
            privateCollections.push(collection)
          } else {
            publicCollections.push(collection)
          }
        })

        // set private user collections
        this.privateUserCollections$.next(privateCollections)

        // set public user collections
        this.publicUserCollections$.next(publicCollections)
      })
  }

  /**
   * Adds educational material(s) to selected collection.
   * @param {AddToCollectionPost} payload
   * @returns {Observable<AddToCollectionResponse>} Response
   */
  addToCollection(payload: AddToCollectionPost): Observable<AddToCollectionResponse> {
    return this.http
      .post<AddToCollectionResponse>(`${urls.backendUrl}/collection/addMaterial`, payload, {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      })
      .pipe(catchError(this.handleError))
  }

  /**
   * Removes educational material(s) from selected collection.
   * @param {RemoveFromCollectionPost} payload
   * @returns {Observable<RemoveFromCollectionResponse>} Response
   */
  removeFromCollection(
    payload: RemoveFromCollectionPost
  ): Observable<RemoveFromCollectionResponse> {
    return this.http
      .post<RemoveFromCollectionResponse>(`${urls.backendUrl}/collection/removeMaterial`, payload, {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      })
      .pipe(catchError(this.handleError))
  }

  /**
   * Updates collection details.
   * @param {string} collectionId
   */
  updateCollection(collectionId: string): void {
    this.http
      .get<CollectionResponse>(`${urls.backendUrl}/collection/getCollection/${collectionId}`, {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      })
      .subscribe((collectionResponse: CollectionResponse) => {
        const alignmentObjects = this.extractAlignmentObjects(collectionResponse.alignmentObjects)

        const materialsAndHeadings: CollectionFormMaterialAndHeading[] = []

        collectionResponse.educationalmaterials.forEach((material) => {
          materialsAndHeadings.push({
            id: material.id,
            priority: material.priority
          })
        })

        collectionResponse.headings.forEach((heading) => {
          materialsAndHeadings.push({
            heading: heading.heading,
            description: heading.description,
            priority: heading.priority
          })
        })

        materialsAndHeadings.sort((a, b) => a.priority - b.priority)

        const collection: Collection = {
          id: collectionResponse.collection.id,
          publishedAt: collectionResponse.collection.publishedat,
          updatedAt: collectionResponse.collection.updatedat,
          createdAt: collectionResponse.collection.createdat,
          name: collectionResponse.collection.name,
          description: collectionResponse.collection.description,
          keywords: collectionResponse.keywords,
          languages: collectionResponse.languages,
          educationalRoles: collectionResponse.educationalRoles,
          educationalUses: collectionResponse.educationalUses,
          educationalMaterials: collectionResponse.educationalmaterials,
          materialsAndHeadings: materialsAndHeadings,
          accessibilityFeatures: collectionResponse.accessibilityFeatures,
          accessibilityHazards: collectionResponse.accessibilityHazards,
          educationalLevels: collectionResponse.educationalLevels,
          earlyChildhoodEducationSubjects: alignmentObjects.earlyChildhoodEducationSubjects,
          earlyChildhoodEducationObjectives: alignmentObjects.earlyChildhoodEducationObjectives,
          earlyChildhoodEducationFramework:
            alignmentObjects.earlyChildhoodEducationSubjects[0]?.educationalFramework,
          prePrimaryEducationSubjects: alignmentObjects.prePrimaryEducationSubjects,
          prePrimaryEducationObjectives: alignmentObjects.prePrimaryEducationObjectives,
          prePrimaryEducationFramework:
            alignmentObjects.prePrimaryEducationSubjects[0]?.educationalFramework,
          basicStudySubjects: alignmentObjects.basicStudySubjects,
          basicStudyObjectives: alignmentObjects.basicStudyObjectives,
          basicStudyContents: alignmentObjects.basicStudyContents,
          basicStudyFramework: alignmentObjects.basicStudySubjects[0]?.educationalFramework,
          preparatoryEducationSubjects: alignmentObjects.preparatoryEducationSubjects,
          preparatoryEducationObjectives: alignmentObjects.preparatoryEducationObjectives,
          upperSecondarySchoolSubjectsOld: alignmentObjects.upperSecondarySchoolSubjectsOld,
          upperSecondarySchoolCoursesOld: alignmentObjects.upperSecondarySchoolCoursesOld,
          upperSecondarySchoolObjectives: alignmentObjects.upperSecondarySchoolObjectives,
          upperSecondarySchoolFramework:
            alignmentObjects.upperSecondarySchoolSubjectsOld[0]?.educationalFramework,
          upperSecondarySchoolSubjectsNew: alignmentObjects.upperSecondarySchoolSubjectsNew,
          upperSecondarySchoolModulesNew: alignmentObjects.upperSecondarySchoolModulesNew,
          upperSecondarySchoolObjectivesNew: alignmentObjects.upperSecondarySchoolObjectivesNew,
          upperSecondarySchoolContentsNew: alignmentObjects.upperSecondarySchoolContentsNew,
          newUpperSecondarySchoolFramework:
            alignmentObjects.upperSecondarySchoolSubjectsNew[0]?.educationalFramework,
          vocationalDegrees: alignmentObjects.vocationalDegrees,
          vocationalUnits: alignmentObjects.vocationalUnits,
          subjectOfCommonUnit: alignmentObjects.subjectOfCommonUnit,
          vocationalRequirements: alignmentObjects.vocationalRequirements,
          vocationalEducationFramework: alignmentObjects.vocationalDegrees[0]?.educationalFramework,
          selfMotivatedEducationSubjects: alignmentObjects.selfMotivatedEducationSubjects,
          selfMotivatedEducationObjectives: alignmentObjects.selfMotivatedEducationObjectives,
          scienceBranches: alignmentObjects.scienceBranches,
          scienceBranchObjectives: alignmentObjects.scienceBranchObjectives,
          higherEducationFramework: alignmentObjects.scienceBranches[0]?.educationalFramework,
          owner: collectionResponse.owner,
          authors: collectionResponse.authors,
          thumbnail: collectionResponse.thumbnail
            ? collectionResponse.thumbnail
            : 'assets/img/thumbnails/kokoelma.png'
        }

        this.collection$.next(collection)
      })
  }

  /**
   * Updates edit collection.
   * @param {string} collectionId
   */
  updateEditCollection(collectionId: string): void {
    this.http
      .get<CollectionResponse>(`${urls.backendUrl}/collection/getCollection/${collectionId}`, {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      })
      .subscribe((collection: CollectionResponse) => {
        const alignmentObjects = this.extractAlignmentObjects(collection.alignmentObjects)
        const materialsAndHeadings: CollectionFormMaterialAndHeading[] = []

        collection.educationalmaterials.forEach((material) => {
          materialsAndHeadings.push({
            id: material.id,
            priority: material.priority
          })
        })

        collection.headings.forEach((heading) => {
          materialsAndHeadings.push({
            heading: heading.heading,
            description: heading.description,
            priority: heading.priority
          })
        })

        materialsAndHeadings.sort((a, b) => a.priority - b.priority)

        const collectionForm: CollectionForm = {
          id: collection.collection.id,
          isPublished: collection.collection.publishedat !== null,
          name: collection.collection.name,
          thumbnail: collection.thumbnail
            ? collection.thumbnail
            : 'assets/img/thumbnails/kokoelma.png',
          keywords: collection.keywords,
          languages: collection.languages,
          educationalRoles: collection.educationalRoles,
          educationalUses: collection.educationalUses,
          accessibilityFeatures: collection.accessibilityFeatures,
          accessibilityHazards: collection.accessibilityHazards,
          materials: collection.educationalmaterials.map(
            (material): CollectionFormMaterial => ({
              id: material.id,
              authors: material.author.map(
                (author): CollectionFormMaterialAuthor => ({
                  author: author.authorname,
                  organization: {
                    key: author.organizationkey,
                    value: author.organization
                  }
                })
              ),
              license: material.license,
              name: material.name,
              priority: material.priority,
              publishedAt: material.publishedat,
              description: material.description,
              thumbnail: material.thumbnail?.thumbnail
                ? material.thumbnail.thumbnail
                : `assets/img/thumbnails/${material.learningResourceTypes[0].learningresourcetypekey}.png`,
              learningResourceTypes: material.learningResourceTypes.map((type) => ({
                key: type.learningresourcetypekey,
                value: type.value
              }))
            })
          ),
          materialsAndHeadings,
          description: collection.collection.description,
          educationalLevels: collection.educationalLevels,
          earlyChildhoodEducationSubjects: alignmentObjects.earlyChildhoodEducationSubjects,
          earlyChildhoodEducationObjectives: alignmentObjects.earlyChildhoodEducationObjectives,
          earlyChildhoodEducationFramework:
            alignmentObjects.earlyChildhoodEducationSubjects[0]?.educationalFramework,
          prePrimaryEducationSubjects: alignmentObjects.prePrimaryEducationSubjects,
          prePrimaryEducationObjectives: alignmentObjects.prePrimaryEducationObjectives,
          prePrimaryEducationFramework:
            alignmentObjects.prePrimaryEducationSubjects[0]?.educationalFramework,
          basicStudySubjects: alignmentObjects.basicStudySubjects,
          basicStudyObjectives: alignmentObjects.basicStudyObjectives,
          basicStudyContents: alignmentObjects.basicStudyContents,
          basicStudyFramework: alignmentObjects.basicStudySubjects[0]?.educationalFramework,
          preparatoryEducationSubjects: alignmentObjects.preparatoryEducationSubjects,
          preparatoryEducationObjectives: alignmentObjects.preparatoryEducationObjectives,
          currentUpperSecondarySchoolSelected:
            alignmentObjects.upperSecondarySchoolSubjectsOld.length > 0,
          newUpperSecondarySchoolSelected:
            alignmentObjects.upperSecondarySchoolSubjectsNew.length > 0,
          upperSecondarySchoolSubjectsOld: alignmentObjects.upperSecondarySchoolSubjectsOld,
          upperSecondarySchoolCoursesOld: alignmentObjects.upperSecondarySchoolCoursesOld,
          upperSecondarySchoolObjectives: alignmentObjects.upperSecondarySchoolObjectives,
          upperSecondarySchoolFramework:
            alignmentObjects.upperSecondarySchoolSubjectsOld[0]?.educationalFramework,
          upperSecondarySchoolSubjectsNew: alignmentObjects.upperSecondarySchoolSubjectsNew,
          upperSecondarySchoolModulesNew: alignmentObjects.upperSecondarySchoolModulesNew,
          upperSecondarySchoolObjectivesNew: alignmentObjects.upperSecondarySchoolObjectivesNew,
          upperSecondarySchoolContentsNew: alignmentObjects.upperSecondarySchoolContentsNew,
          newUpperSecondarySchoolFramework:
            alignmentObjects.upperSecondarySchoolSubjectsNew[0]?.educationalFramework,
          vocationalDegrees: alignmentObjects.vocationalDegrees,
          vocationalUnits: alignmentObjects.vocationalUnits,
          subjectOfCommonUnit: alignmentObjects.subjectOfCommonUnit,
          vocationalRequirements: alignmentObjects.vocationalRequirements,
          vocationalEducationFramework: alignmentObjects.vocationalDegrees[0]?.educationalFramework,
          selfMotivatedEducationSubjects: alignmentObjects.selfMotivatedEducationSubjects,
          selfMotivatedEducationObjectives: alignmentObjects.selfMotivatedEducationObjectives,
          scienceBranches: alignmentObjects.scienceBranches,
          scienceBranchObjectives: alignmentObjects.scienceBranchObjectives,
          higherEducationFramework: alignmentObjects.scienceBranches[0]?.educationalFramework
        }

        this.editCollection$.next(collectionForm)
      })
  }

  /**
   * Updates collection details.
   * @param {UpdateCollectionPut} collection
   */
  updateCollectionDetails(collection: UpdateCollectionPut): Observable<any> {
    return this.http
      .put(`${urls.backendUrl}/collection/update`, collection)
      .pipe(catchError(this.handleError))
  }

  /**
   * Uploads thumbnail image for collection.
   * @param {string} base64Image
   * @param {string} collectionId
   * @returns {Observable<UploadMessage>} Upload message
   */
  uploadImage(base64Image: string, collectionId: string): Observable<UploadMessage> {
    return this.http
      .post<{ base64image: string }>(
        `${urls.backendUrlV2}/collection/${collectionId}/thumbnail`,
        {
          base64image: base64Image
        },
        {
          headers: new HttpHeaders({
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }),
          reportProgress: true,
          observe: 'events'
        }
      )
      .pipe(
        map((event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              if (event.total) {
                const progress = Math.round((100 * event.loaded) / event.total)
                return { status: 'progress', message: progress }
              } else {
                return { status: 'progress', message: 'Calculating...' }
              }

            case HttpEventType.Response:
              return { status: 'completed', message: event.body }

            default:
              return { status: 'error', message: `Unhandled event: ${event.type}` }
          }
        }),
        catchError(this.handleError)
      )
  }

  /**
   * Updates recent collections.
   */
  updateRecentCollections(): void {
    this.http
      .get<RecentCollectionsResponse>(`${urls.backendUrl}/collection/recentCollection`, {
        headers: new HttpHeaders({
          Accept: 'application/json'
        })
      })
      .subscribe((response: RecentCollectionsResponse) => {
        const collections = response.collections.map((collection: RecentCollectionResponse) => ({
          id: collection.id,
          name: collection.name,
          thumbnail: collection.thumbnail
            ? collection.thumbnail
            : 'assets/img/thumbnails/kokoelma.png',
          authors: collection.authors,
          description: collection.description,
          keywords: collection.keywords,
          educationalLevels: collection.educationalLevels,
          languages: collection.languages
        }))

        this.recentCollections$.next(collections)
      })
  }

  /**
   * Extracts different alignment objects from object array.
   * @param {any} alignmentObjects
   * @returns {AlignmentObjects} Alignment objects
   * @private
   */
  private extractAlignmentObjects(alignmentObjects: any): AlignmentObjects {
    const earlyChildhoodEducationSubjects: AlignmentObjectExtended[] = []
    const earlyChildhoodEducationObjectives: AlignmentObjectExtended[] = []
    const prePrimaryEducationSubjects: AlignmentObjectExtended[] = []
    const prePrimaryEducationObjectives: AlignmentObjectExtended[] = []
    const basicStudySubjects: AlignmentObjectExtended[] = []
    const basicStudyObjectives: AlignmentObjectExtended[] = []
    const basicStudyContents: AlignmentObjectExtended[] = []
    const preparatoryEducationSubjects: AlignmentObjectExtended[] = []
    const preparatoryEducationObjectives: AlignmentObjectExtended[] = []
    const upperSecondarySchoolSubjectsOld: AlignmentObjectExtended[] = []
    const upperSecondarySchoolCoursesOld: AlignmentObjectExtended[] = []
    const upperSecondarySchoolObjectives: AlignmentObjectExtended[] = []
    const upperSecondarySchoolSubjectsNew: AlignmentObjectExtended[] = []
    const upperSecondarySchoolModulesNew: AlignmentObjectExtended[] = []
    const upperSecondarySchoolObjectivesNew: AlignmentObjectExtended[] = []
    const upperSecondarySchoolContentsNew: AlignmentObjectExtended[] = []
    const vocationalDegrees: AlignmentObjectExtended[] = []
    const vocationalUnits: AlignmentObjectExtended[] = []
    const subjectOfCommonUnit: AlignmentObjectExtended[] = []
    const vocationalRequirements: AlignmentObjectExtended[] = []
    const selfMotivatedEducationSubjects: AlignmentObjectExtended[] = []
    const selfMotivatedEducationObjectives: AlignmentObjectExtended[] = []
    const scienceBranches: AlignmentObjectExtended[] = []
    const scienceBranchObjectives: AlignmentObjectExtended[] = []

    alignmentObjects
      .map(
        (aObject: any): AlignmentObjectExtended => ({
          alignmentType: aObject.alignmenttype,
          educationalFramework: aObject.educationalframework,
          key: aObject.objectkey,
          source: aObject.source,
          targetName: aObject.targetname,
          targetUrl: aObject.targeturl
        })
      )
      .forEach((aObject: AlignmentObjectExtended) => {
        switch (aObject.source) {
          case koodistoSources.earlyChildhoodSubjects:
            earlyChildhoodEducationSubjects.push(aObject)
            break

          case koodistoSources.earlyChildhoodObjectives:
            earlyChildhoodEducationObjectives.push(aObject)
            break

          case koodistoSources.prePrimarySubjects:
            prePrimaryEducationSubjects.push(aObject)
            break

          case koodistoSources.prePrimaryObjectives:
            prePrimaryEducationObjectives.push(aObject)
            break

          case koodistoSources.basicStudySubjects:
            basicStudySubjects.push(aObject)
            break

          case koodistoSources.basicStudyObjectives:
            basicStudyObjectives.push(aObject)
            break

          case koodistoSources.basicStudyContents:
            basicStudyContents.push(aObject)
            break

          case koodistoSources.preparatoryEducationSubjects:
            preparatoryEducationSubjects.push(aObject)
            break

          case koodistoSources.preparatoryEducationObjectives:
            preparatoryEducationObjectives.push(aObject)
            break

          case koodistoSources.upperSecondarySubjectsOld:
            upperSecondarySchoolSubjectsOld.push(aObject)
            break

          case koodistoSources.upperSecondaryCoursesOld:
            upperSecondarySchoolCoursesOld.push(aObject)
            break

          case koodistoSources.upperSecondarySubjects:
            upperSecondarySchoolCoursesOld.push(aObject)
            break

          case koodistoSources.upperSecondaryObjectives:
            upperSecondarySchoolObjectives.push(aObject)
            break

          case koodistoSources.upperSecondarySubjectsNew:
            upperSecondarySchoolSubjectsNew.push(aObject)
            break

          case koodistoSources.upperSecondaryModulesNew:
            upperSecondarySchoolModulesNew.push(aObject)
            break

          case koodistoSources.upperSecondaryObjectivesNew:
            upperSecondarySchoolObjectivesNew.push(aObject)
            break

          case koodistoSources.upperSecondaryContentsNew:
            upperSecondarySchoolContentsNew.push(aObject)
            break

          case koodistoSources.vocationalDegrees:
            vocationalDegrees.push(aObject)
            break

          case koodistoSources.vocationalUnits:
            vocationalUnits.push(aObject)
            break

          case koodistoSources.subjectOfCommonUnit:
            subjectOfCommonUnit.push(aObject)
            break

          case koodistoSources.vocationalObjectives:
            vocationalRequirements.push(aObject)
            break

          case koodistoSources.vocationalRequirements:
            vocationalRequirements.push(aObject)
            break

          case koodistoSources.selfMotivatedSubjects:
            selfMotivatedEducationSubjects.push(aObject)
            break

          case koodistoSources.selfMotivatedObjectives:
            selfMotivatedEducationObjectives.push(aObject)
            break

          case koodistoSources.scienceBranches:
            scienceBranches.push(aObject)
            break

          case koodistoSources.scienceBranchObjectives:
            scienceBranchObjectives.push(aObject)
            break
        }
      })

    return {
      earlyChildhoodEducationSubjects,
      earlyChildhoodEducationObjectives,
      prePrimaryEducationSubjects,
      prePrimaryEducationObjectives,
      basicStudySubjects,
      basicStudyObjectives,
      basicStudyContents,
      preparatoryEducationSubjects,
      preparatoryEducationObjectives,
      upperSecondarySchoolSubjectsOld,
      upperSecondarySchoolCoursesOld,
      upperSecondarySchoolObjectives,
      upperSecondarySchoolSubjectsNew,
      upperSecondarySchoolModulesNew,
      upperSecondarySchoolObjectivesNew,
      upperSecondarySchoolContentsNew,
      vocationalDegrees,
      vocationalUnits,
      subjectOfCommonUnit,
      vocationalRequirements,
      selfMotivatedEducationSubjects,
      selfMotivatedEducationObjectives,
      scienceBranches,
      scienceBranchObjectives
    }
  }
}
